import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Validate textarea length
    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      setFormStatus('error');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d518eitpfatkmn6ifbr0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 3000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  const faqs = [
    {
      question: 'What is a VPN and why do I need it?',
      answer: 'A VPN (Virtual Private Network) creates a secure, encrypted connection between your device and the internet. It protects your online privacy, secures your data from hackers, and allows you to browse anonymously. With FREN VPN, your internet activity remains private and your personal information stays protected.'
    },
    {
      question: 'How does FREN VPN protect my privacy?',
      answer: 'FREN VPN uses military-grade AES-256 encryption to secure your internet connection. We maintain a strict zero-log policy, meaning we never track, collect, or share your online activities. Your IP address is masked, making your online presence completely anonymous.'
    },
    {
      question: 'Can I use FREN VPN on multiple devices?',
      answer: 'Yes! FREN VPN supports all major platforms including Windows, macOS, iOS, Android, and Linux. You can protect multiple devices simultaneously with a single account, ensuring all your devices are secure wherever you go.'
    },
    {
      question: 'Will FREN VPN slow down my internet speed?',
      answer: 'FREN VPN is optimized for speed with our global network of high-performance servers. While encryption may cause minimal speed reduction, most users experience no noticeable difference. Our smart server selection automatically connects you to the fastest available server.'
    },
    {
      question: 'Where are your servers located?',
      answer: 'FREN VPN operates a global network of servers across multiple continents, including Europe, North America, Asia, and Australia. This ensures you always have access to fast, reliable connections no matter where you are in the world.'
    },
    {
      question: 'Is FREN VPN legal to use?',
      answer: 'Yes, using a VPN is legal in most countries, including Croatia and the European Union. FREN VPN is designed to protect your privacy and security online. However, we do not condone using our service for illegal activities.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/90 backdrop-blur-sm'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img src="https://public.readdy.ai/ai/img_res/f0bf99b8-4f3f-4544-8b1a-f8b127dc5bd2.png" alt="FREN d.o.o. Logo" className="h-10 w-10 object-contain" />
              <span className="text-2xl font-bold text-gray-900">FREN</span>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-indigo-600 transition-colors cursor-pointer whitespace-nowrap">Home</button>
              <span className="text-gray-300">|</span>
              <button onClick={() => scrollToSection('vpn')} className="text-gray-700 hover:text-indigo-600 transition-colors cursor-pointer whitespace-nowrap">VPN</button>
              <span className="text-gray-300">|</span>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-indigo-600 transition-colors cursor-pointer whitespace-nowrap">About Us</button>
              <span className="text-gray-300">|</span>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-indigo-600 transition-colors cursor-pointer whitespace-nowrap">Contact</button>
              <span className="text-gray-300">|</span>
              <button onClick={() => scrollToSection('faq')} className="text-gray-700 hover:text-indigo-600 transition-colors cursor-pointer whitespace-nowrap">FAQ</button>
            </div>

            <a href="#contact" className="hidden md:block bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-2.5 rounded-lg hover:shadow-lg transition-all duration-300 cursor-pointer whitespace-nowrap">Get Started</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative pt-32 pb-20 px-6 overflow-hidden bg-gradient-to-b from-blue-50 to-white">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50/50 via-white/30 to-white"></div>
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <h1 className="text-7xl md:text-8xl font-bold text-gray-900 leading-tight mb-6">
            Secure Your<br />
            Digital Freedom<br />
            Everywhere
          </h1>
          <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
            Experience true online privacy with military-grade encryption.<br />
            Browse anonymously, access content freely, and protect your data from prying eyes.
          </p>
          <button onClick={() => scrollToSection('contact')} className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-10 py-4 rounded-2xl text-lg font-semibold hover:shadow-2xl transition-all duration-300 inline-flex items-center space-x-3 cursor-pointer whitespace-nowrap">
            <span>Get Started</span>
            <i className="ri-arrow-right-line text-xl"></i>
          </button>

          <div className="mt-16 relative">
            <div className="w-full h-96 flex items-center justify-center">
              <img 
                src="https://public.readdy.ai/ai/img_res/f9c14450a9247387047319c5403255ea.jpg" 
                alt="FREN VPN App Interface" 
                className="w-80 h-auto object-contain transform rotate-3 hover:rotate-0 transition-transform duration-500 drop-shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* VPN Features Section */}
      <section id="vpn" className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 border border-indigo-200 rounded-full text-indigo-600 text-sm font-semibold uppercase tracking-wide mb-6">
              Why Choose FREN
            </div>
            <h2 className="text-5xl font-bold text-gray-900 mb-6">
              Enterprise-Grade Security<br />
              Consumer-Simple Experience
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              FREN VPN combines cutting-edge security technology with an intuitive interface, making online privacy accessible to everyone.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            <div className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Military-Grade Encryption</h3>
              <p className="text-gray-600 leading-relaxed">
                Your data is protected with AES-256 encryption, the same standard used by governments and security experts worldwide. Every bit of information transmitted through FREN VPN is completely secure and unreadable to third parties.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-global-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Global Server Network</h3>
              <p className="text-gray-600 leading-relaxed">
                Access our extensive network of high-speed servers located across multiple continents. Whether you're at home or traveling abroad, enjoy fast, reliable connections with unlimited bandwidth and no restrictions.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-eye-off-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Zero-Log Policy</h3>
              <p className="text-gray-600 leading-relaxed">
                We never track, collect, or store your online activities. Your browsing history, connection logs, and personal data remain completely private. What you do online is your business, and we keep it that way.
              </p>
            </div>
          </div>

          <div className="mt-20 grid md:grid-cols-2 gap-10">
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8">
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-6 shadow-md">
                <i className="ri-speed-line text-3xl text-indigo-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Lightning-Fast Speeds</h3>
              <p className="text-gray-600 leading-relaxed">
                Stream, download, and browse without slowdowns. Our optimized servers ensure you get the fastest possible connection speeds while maintaining maximum security.
              </p>
            </div>

            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8">
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-6 shadow-md">
                <i className="ri-device-line text-3xl text-indigo-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Multi-Device Support</h3>
              <p className="text-gray-600 leading-relaxed">
                Protect all your devices with one account. FREN VPN works seamlessly on Windows, macOS, iOS, Android, and Linux, keeping you secure on every platform.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-24 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-5 gap-12 items-center">
            <div className="md:col-span-2">
              <div className="relative w-full h-96 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center">
                  <img 
                    src="https://readdy.ai/api/search-image?query=abstract%20geometric%20network%20visualization%20with%20interconnected%20nodes%20and%20flowing%20data%20streams%2C%20modern%20tech%20illustration%20representing%20digital%20security%20and%20global%20connectivity%2C%20layered%20translucent%20shapes%20in%20blue%20and%20purple%20tones%2C%20minimalist%20design%20with%20depth%20and%20dimension%2C%20professional%20corporate%20aesthetic&width=600&height=700&seq=about-visual-001&orientation=portrait" 
                    alt="FREN Network Visualization" 
                    className="w-full h-full object-cover opacity-80"
                  />
                </div>
              </div>
            </div>

            <div className="md:col-span-3">
              <div className="border-l-4 border-indigo-600 pl-6 mb-8">
                <p className="text-indigo-600 font-semibold uppercase tracking-wide text-sm mb-2">About FREN d.o.o.</p>
              </div>
              <h2 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Croatian Innovation<br />
                Global Protection
              </h2>
              <div className="space-y-6 text-gray-600 leading-relaxed text-lg">
                <p>
                  FREN d.o.o. is a Croatian technology company dedicated to protecting digital privacy and freedom. Based in Bibinje, Croatia, we develop innovative security solutions that empower individuals and businesses to take control of their online presence.
                </p>
                <p>
                  Our flagship product, FREN VPN, represents our commitment to making enterprise-level security accessible to everyone. We believe that privacy is a fundamental right, not a luxury, and our mission is to provide tools that protect this right without compromise.
                </p>
                <p>
                  With a team of experienced developers and security experts, we continuously innovate to stay ahead of emerging threats. Our zero-log policy and transparent practices ensure that your trust in us is well-placed. We don't just promise privacy – we engineer it into every aspect of our service.
                </p>
                <p>
                  Join thousands of users worldwide who trust FREN VPN to protect their digital lives. Experience the perfect balance of security, speed, and simplicity.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">Get In Touch</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Have questions or need support? We're here to help. Reach out to our team and we'll get back to you as soon as possible.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-2xl text-indigo-600"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Email</h3>
                  <a href="mailto:info@frendo-o.com" className="text-indigo-600 hover:text-indigo-700 transition-colors cursor-pointer">info@frendo-o.com</a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-2xl text-indigo-600"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Phone</h3>
                  <a href="tel:+18049068542" className="text-gray-600 hover:text-indigo-600 transition-colors cursor-pointer">+1 8049068542</a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-2xl text-indigo-600"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Address</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Bibinjskih Bojovnika 15<br />
                    23205 Bibinje<br />
                    Croatia
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8">
              <form className="space-y-6" data-readdy-form id="contact-form" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">Name</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent transition-all text-sm" 
                    placeholder="Your name"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">Email</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent transition-all text-sm" 
                    placeholder="your@email.com"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">Message</label>
                  <textarea 
                    id="message" 
                    name="message"
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent transition-all text-sm resize-none" 
                    placeholder="How can we help you?"
                    required
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>

                {formStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                    Thank you! Your message has been sent successfully.
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                    Sorry, there was an error sending your message. Please try again.
                  </div>
                )}

                <button 
                  type="submit" 
                  disabled={formStatus === 'submitting'}
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">
              Find answers to common questions about FREN VPN and our services.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <h4 className="text-lg font-semibold text-gray-900 pr-8">{faq.question}</h4>
                  <i className={`ri-arrow-${openFaq === index ? 'up' : 'down'}-s-line text-2xl text-indigo-600 flex-shrink-0`}></i>
                </button>
                {openFaq === index && (
                  <div className="px-8 pb-6">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-gradient-to-br from-gray-900 via-indigo-900 to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-32 h-32 bg-white rounded-full"></div>
          <div className="absolute top-40 right-32 w-24 h-24 bg-white rounded-full"></div>
          <div className="absolute bottom-32 left-1/3 w-16 h-16 bg-white rounded-full"></div>
          <div className="absolute bottom-20 right-20 w-20 h-20 bg-white rounded-full"></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-6xl font-bold text-white mb-6 leading-tight">
            Ready to Protect<br />
            Your Privacy?
          </h2>
          <p className="text-xl text-gray-300 mb-10">
            Join thousands of users who trust FREN VPN for their online security
          </p>
          <button onClick={() => scrollToSection('contact')} className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-12 py-5 rounded-2xl text-lg font-semibold hover:shadow-2xl transition-all duration-300 inline-flex items-center space-x-3 cursor-pointer whitespace-nowrap">
            <i className="ri-download-line text-2xl"></i>
            <span>Download Now</span>
          </button>
        </div>
      </section>

      {/* Terms Section */}
      <section id="terms" className="py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-5xl font-bold text-gray-900 mb-12">Terms of Service</h2>
          
          <div className="prose prose-lg max-w-none space-y-8 text-gray-600">
            <div>
              <h4 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h4>
              <p className="leading-relaxed">
                By accessing and using FREN VPN services, you accept and agree to be bound by the terms and provisions of this agreement. If you do not agree to these terms, please do not use our services.
              </p>
            </div>

            <div>
              <h4 className="text-2xl font-semibold text-gray-900 mb-4">2. Service Description</h4>
              <p className="leading-relaxed">
                FREN VPN provides virtual private network services designed to protect your online privacy and security. We offer encrypted connections through our global server network, ensuring your internet activity remains private and secure.
              </p>
            </div>

            <div>
              <h4 className="text-2xl font-semibold text-gray-900 mb-4">3. Privacy Policy</h4>
              <p className="leading-relaxed">
                We maintain a strict zero-log policy. FREN d.o.o. does not collect, store, or share any information about your online activities. Your privacy is our top priority, and we are committed to protecting your personal data in accordance with GDPR and other applicable privacy regulations.
              </p>
            </div>

            <div>
              <h4 className="text-2xl font-semibold text-gray-900 mb-4">4. Acceptable Use</h4>
              <p className="leading-relaxed">
                You agree to use FREN VPN services only for lawful purposes. You are prohibited from using our services for any illegal activities, including but not limited to: unauthorized access to computer systems, distribution of malware, copyright infringement, or any activity that violates local, national, or international laws.
              </p>
            </div>

            <div>
              <h4 className="text-2xl font-semibold text-gray-900 mb-4">5. Account Security</h4>
              <p className="leading-relaxed">
                You are responsible for maintaining the confidentiality of your account credentials. Any activities that occur under your account are your responsibility. Please notify us immediately if you suspect any unauthorized use of your account.
              </p>
            </div>

            <div>
              <h4 className="text-2xl font-semibold text-gray-900 mb-4">6. Service Availability</h4>
              <p className="leading-relaxed">
                While we strive to provide uninterrupted service, FREN d.o.o. does not guarantee that our services will be available at all times. We reserve the right to modify, suspend, or discontinue any aspect of our services with or without notice.
              </p>
            </div>

            <div>
              <h4 className="text-2xl font-semibold text-gray-900 mb-4">7. Limitation of Liability</h4>
              <p className="leading-relaxed">
                FREN d.o.o. shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use or inability to use our services. Our total liability shall not exceed the amount you paid for the service.
              </p>
            </div>

            <div>
              <h4 className="text-2xl font-semibold text-gray-900 mb-4">8. Modifications to Terms</h4>
              <p className="leading-relaxed">
                We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting to our website. Your continued use of FREN VPN services after any changes constitutes acceptance of the new terms.
              </p>
            </div>

            <div>
              <h4 className="text-2xl font-semibold text-gray-900 mb-4">9. Governing Law</h4>
              <p className="leading-relaxed">
                These terms shall be governed by and construed in accordance with the laws of the Republic of Croatia. Any disputes arising from these terms or your use of our services shall be subject to the exclusive jurisdiction of the courts of Croatia.
              </p>
            </div>

            <div>
              <h4 className="text-2xl font-semibold text-gray-900 mb-4">10. Contact Information</h4>
              <p className="leading-relaxed">
                If you have any questions about these Terms of Service, please contact us at info@frendo-o.com or at our address: Bibinjskih Bojovnika 15, 23205 Bibinje, Croatia.
              </p>
            </div>

            <div className="pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Last updated: January 2025
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 pb-12 border-b border-gray-800">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <img src="https://public.readdy.ai/ai/img_res/f0bf99b8-4f3f-4544-8b1a-f8b127dc5bd2.png" alt="FREN Logo" className="h-10 w-10 object-contain" />
              <div>
                <span className="text-2xl font-bold text-white">FREN</span>
                <p className="text-sm text-gray-400">Securing digital lives</p>
              </div>
            </div>
            <p className="text-gray-400 text-sm">Securing digital lives since 2025</p>
          </div>

          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <h3 className="text-white text-lg font-semibold mb-4">Product</h3>
              <ul className="space-y-3">
                <li><button onClick={() => scrollToSection('vpn')} className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">VPN Features</button></li>
                <li><button onClick={() => scrollToSection('vpn')} className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">Security</button></li>
                <li><button onClick={() => scrollToSection('vpn')} className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">Servers</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">Download</button></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white text-lg font-semibold mb-4">Company</h3>
              <ul className="space-y-3">
                <li><button onClick={() => scrollToSection('about')} className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">About Us</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">Contact</button></li>
                <li><button onClick={() => scrollToSection('faq')} className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">FAQ</button></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white text-lg font-semibold mb-4">Legal</h3>
              <ul className="space-y-3">
                <li><button onClick={() => scrollToSection('terms')} className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">Terms of Service</button></li>
                <li><button onClick={() => scrollToSection('terms')} className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">Privacy Policy</button></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white text-lg font-semibold mb-4">Contact Us</h3>
              <ul className="space-y-3 text-sm">
                <li>
                  <a href="mailto:info@frendo-o.com" className="text-indigo-400 hover:text-indigo-300 transition-colors cursor-pointer">info@frendo-o.com</a>
                </li>
                <li className="text-gray-400">+1 8049068542</li>
                <li className="text-gray-400">
                  Bibinjskih Bojovnika 15<br />
                  23205 Bibinje<br />
                  Croatia
                </li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center pt-8 border-t border-gray-800">
            <p className="text-sm text-gray-400 mb-4 md:mb-0">
              © 2025 FREN d.o.o. All rights reserved. | <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Powered by Readdy</a>
            </p>
            <div className="flex items-center space-x-5">
              <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-indigo-600 transition-colors cursor-pointer">
                <i className="ri-linkedin-fill text-lg"></i>
              </a>
              <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-indigo-600 transition-colors cursor-pointer">
                <i className="ri-twitter-x-fill text-lg"></i>
              </a>
              <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-indigo-600 transition-colors cursor-pointer">
                <i className="ri-facebook-fill text-lg"></i>
              </a>
              <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-indigo-600 transition-colors cursor-pointer">
                <i className="ri-instagram-fill text-lg"></i>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
